/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  KeyboardEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react'
import { CountrySelect } from '@/components/country-select'
import TextInput, { TextInputProps } from '@/components/text-input'
import countries from '@/core/constants/countries.json'
import translate from '@/core/services/translate'
import { getTranslation } from '@/account/services/auth'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'

export interface CountryAndPhone {
  countryCode?: string | undefined
  countryCodeValue?: string | undefined
  phoneNumber?: string
  phoneNumberConfirm?: string
}

interface PhoneNumberCountry {
  value?: CountryAndPhone
  onChange: (value: CountryAndPhone) => void
  onBlur?: () => void
  error?: string
  disabled?: boolean
  phonePlaceholder?: string
  onChangeCountry?: (value: CountryAndPhone) => void
  textInputProps?: TextInputProps
  buttonClassName?: string
  labelClassName?: string
}

interface PhoneNumberCountryRef {
  focus: () => void
}

export const PhoneNumberCountry = forwardRef<
  PhoneNumberCountryRef,
  PhoneNumberCountry
>(
  (
    {
      value,
      onChange,
      error,
      disabled = false,
      phonePlaceholder,
      onChangeCountry,
      textInputProps,
      buttonClassName,
      labelClassName,
    },
    passedRef,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(passedRef, () => ({
      focus: () => {
        inputRef.current?.focus()
      },
    }))

    const language = getTranslation()

    const [countryCode, setCountryCode] = useState<string | undefined>()
    const [countryCodeValue, setCountryCodeValue] = useState<
      string | undefined
    >(value?.countryCodeValue)
    const [phoneNumber, setPhoneNumber] = useState<string>(
      value?.phoneNumber || '',
    )

    useEffect(() => {
      if (value?.countryCode) {
        setCountryCode(value.countryCode || value.countryCodeValue)
        setPhoneNumber(value.phoneNumber || '')
        setCountryCodeValue(value.countryCodeValue)
      } else {
        setCountryCode(undefined)
        setPhoneNumber('')
        setCountryCodeValue(undefined)
      }
    }, [value])

    const validateNumberInput = (event: KeyboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement
      const key = event.key

      if (
        key === 'Backspace' ||
        key === 'Delete' ||
        key.includes('Arrow') ||
        key.includes('Enter')
      ) {
        return
      }

      const number = input.value

      if (!/^\d*$/.test(number)) {
        event.preventDefault()
        return
      }
    }

    const validateInput = (value: any) => {
      if (value.charAt(0) === '0') {
        return false // Invalid input
      }
      return true // Valid input
    }

    return (
      <>
        <CountrySelect
          label={translate(language.value, 'common.country')}
          value={countryCode}
          onChange={(country) => {
            const code = countries.find((c) => c.code === country)?.code
            const phonePrefix = countries.find(
              (c) => c.code === country,
            )?.phonePrefix
            setCountryCode(code)
            setCountryCodeValue(phonePrefix)
            onChange({
              countryCode: code,
              countryCodeValue: phonePrefix,
              phoneNumber,
            })
            onChangeCountry?.({
              countryCode: code,
              countryCodeValue: phonePrefix,
              phoneNumber,
            })
            inputRef.current?.focus()
          }}
          buttonClassName={buttonClassName}
          labelClassName={labelClassName}
          disabled={disabled}
        />
        <TextInput
          prefix={
            countryCodeValue &&
            (countryCodeValue.startsWith('+')
              ? countryCodeValue
              : `+${countryCodeValue}`)
          }
          label={translate(language.value, 'common.phoneNumber')}
          type="text"
          withClear={value?.phoneNumber !== ''}
          clearFunc={() => setPhoneNumber('')}
          disabled={disabled}
          value={phoneNumber}
          onKeyDown={validateNumberInput}
          placeholder={
            phonePlaceholder ||
            translate(language.value, 'common.phoneNumberPlaceholder')
          }
          error={error}
          ref={inputRef}
          onChange={(event) => {
            if (isNaN(Number(event.target.value))) {
              setPhoneNumber((prev) => prev)
              return false
            }

            if (!validateInput(event.target.value)) {
              return false
            }

            if (isValidPhoneNumber(event.target.value)) {
              const parsedPhone = parsePhoneNumber(event.target.value)
              if (parsedPhone) {
                setPhoneNumber(parsedPhone.nationalNumber)
                onChange({
                  countryCode,
                  countryCodeValue,
                  phoneNumber: parsedPhone.nationalNumber,
                })

                return
              }
            }

            const phone = event.target.value.replace(`+${countryCodeValue}`, '')
            setPhoneNumber(phone)
            onChange({
              countryCode,
              countryCodeValue,
              phoneNumber: phone.replace(`+${countryCodeValue}`, ''),
            })
          }}
          {...textInputProps}
        />
      </>
    )
  },
)

PhoneNumberCountry.displayName = 'PhoneNumberCountry'
